<script setup lang="ts">
import { useForm, useField } from 'vee-validate';
import {
  CustomerErhlEquityAccessMethodTooltips,
  CustomerErhlLoanPurposeOptions,
  CustomerErhlLoanPurposeTooltips,
  ErhlEquityAccessMethodOptions,
  ErhlLoanPurpose,
} from '@/helpers/const';
import type { ErhlEquityAccessMethodValue, ErhlLoanPurposeValue } from '@/helpers/const';
import { TippyDirective } from '@/helpers/tippy';
import { useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import { erhlLoanPurposeSchema } from '@/validation';
import { computed, ref } from 'vue';
import api from '@/api';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';

const vTippy = TippyDirective;

const { loanApp, saveAndGoNext } = useErhlCustomerLoanAppProcess();

const isLoading = ref(false);
const { flagSessionExpired } = useSessionExpiry();

const { handleSubmit: validateSubmit } = useForm<{
  erhlLoanPurposes: ErhlLoanPurposeValue[];
  erhlLoanPurposeOtherDescription?: string;
  erhlEquityAccessMethod?: ErhlEquityAccessMethodValue;
}>({
  validationSchema: erhlLoanPurposeSchema,
  initialValues: {
    erhlLoanPurposes: loanApp.value.erhlLoanPurposes ?? [],
    erhlLoanPurposeOtherDescription: loanApp.value.erhlLoanPurposeOtherDescription ?? '',
    erhlEquityAccessMethod: loanApp.value.erhlEquityAccessMethod ?? undefined,
  },
  validateOnMount: false,
});

const { value: equityAccessMethod, errorMessage: equityAccessMethodError } =
  useField<ErhlEquityAccessMethodValue>('erhlEquityAccessMethod');

const { value: selectedPurposes, errorMessage: purposeError } = useField<ErhlLoanPurposeValue[]>('erhlLoanPurposes');

const purposeList = computed(() =>
  (Object.entries(CustomerErhlLoanPurposeOptions) as [ErhlLoanPurposeValue, string][])
    .filter(([value]) => value !== ErhlLoanPurpose.LIFESTYLE_SUPPORT)
    .map(([value, label]) => ({
      value,
      label,
      tooltip: CustomerErhlLoanPurposeTooltips[value],
    })),
);

const isPurposeSelected = (key: ErhlLoanPurposeValue): boolean => (selectedPurposes.value ?? []).includes(key);

/**
 * Toggles a loan purpose in the selected array.
 * Adds if absent, removes if already present.
 */
const togglePurpose = (key: ErhlLoanPurposeValue): void => {
  const current = selectedPurposes.value ?? [];
  selectedPurposes.value = current.includes(key) ? current.filter((p) => p !== key) : [...current, key];
};

/** Show the free-text description field only when 'Other' is selected */
const showOtherField = computed(() => (selectedPurposes.value ?? []).includes(ErhlLoanPurpose.OTHER));

const handleSubmit = validateSubmit(async (formValues) => {
  isLoading.value = true;
  const applicationDraftId = loanApp.value.applicationDraftId;
  const isPurchase = (formValues.erhlLoanPurposes ?? []).includes(ErhlLoanPurpose.PURCHASE_NEW_PROPERTY);
  // Normalise the description once and use it for BOTH the draft save and the step transition, so a
  // non-purchase application without "Other" doesn't leave stale text persisted on the draft.
  const purposeDescription = showOtherField.value
    ? formValues.erhlLoanPurposeOtherDescription
    : isPurchase
    ? loanApp.value.erhlLoanPurposeOtherDescription
    : undefined;
  try {
    saveAndGoNext({
      erhlLoanPurposes: formValues.erhlLoanPurposes,
      erhlLoanPurposeOtherDescription: purposeDescription,
      applicationDraftId,
      erhlEquityAccessMethod: formValues.erhlEquityAccessMethod,
    });
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">What is your <BxHighlight>loan purpose</BxHighlight>?</h2>
    </div>
    <form style="width: 100%; max-width: 760px" @submit="handleSubmit">
      <div class="mb-8" style="max-width: 500px">
        <h3 class="text-md mb-3 font-medium">How do you want to receive your loan funds?</h3>
        <div class="access-method-radio">
          <BxRadio
            id="erhl-access-method"
            v-model="equityAccessMethod"
            label=""
            :options="ErhlEquityAccessMethodOptions"
            :error-message="equityAccessMethodError"
            block
          >
            <template #option-suffix="{ id }">
              <button
                v-tippy="{
                  content: CustomerErhlEquityAccessMethodTooltips[id as ErhlEquityAccessMethodValue],
                  trigger: 'mouseenter',
                  placement: 'right',
                }"
                type="button"
                class="purpose-list__info ml-2"
                :aria-label="`More information about ${CustomerErhlEquityAccessMethodTooltips[id as ErhlEquityAccessMethodValue]}`"
              >
                <BxIcon name="circleInfoLight" class="h-5 w-5" />
              </button>
            </template>
          </BxRadio>
        </div>
      </div>

      <h3 class="text-md mb-1 font-medium">What will you use the funds for?</h3>
      <p class="mb-4 text-sm text-gray-500">(Select all that apply)</p>
      <ul class="purpose-list">
        <li v-for="item in purposeList" :key="item.value" class="purpose-list__item">
          <BxCheckbox
            :id="`erhl-purpose-${item.value}`"
            :model-value="isPurposeSelected(item.value)"
            :label="item.label"
            @update:model-value="togglePurpose(item.value)"
          />
          <button
            v-if="item.tooltip"
            v-tippy="{ content: item.tooltip, trigger: 'mouseenter', placement: 'right' }"
            type="button"
            class="purpose-list__info"
            :aria-label="`More information about ${item.label}`"
          >
            <BxIcon name="circleInfoLight" class="h-5 w-5" />
          </button>
        </li>
      </ul>
      <p v-if="purposeError" class="mt-2 text-sm text-rose-500">{{ purposeError }}</p>

      <BxField v-if="showOtherField" v-slot="{ field }" name="erhlLoanPurposeOtherDescription">
        <BxTextarea
          v-bind="field"
          label="Please give us more details on the loan purpose"
          label-type="floating"
          placeholder="Provide additional details about the loan purpose"
          :rows="5"
          class="form-control mt-4"
        />
      </BxField>

      <div class="mt-6">
        <BxButton variant="primary" :loading="isLoading" type="submit">Save and continue</BxButton>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.access-method-radio {
  :deep(fieldset label) {
    @apply text-base text-bridgit-navy;
  }
}

.purpose-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(5, auto);
  grid-auto-flow: column;
  column-gap: 2rem;
  list-style: none;
  padding: 0;
  margin: 0;

  &__item {
    @apply flex items-center gap-3 py-2;

    &:not(:nth-child(5)):not(:nth-child(10)) {
      @apply border-b border-gray-100;
    }
  }

  &__info {
    @apply flex flex-shrink-0 cursor-pointer items-center text-gray-400 hover:text-gray-600;
    background: none;
    border: none;
    padding: 0;
  }
}
</style>
